import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  FileText, ShieldCheck, Calculator, ListChecks, GitCompare,
  CheckCircle2, XCircle, AlertTriangle, Info, Loader2, ArrowLeft,
  Sparkles, User as UserIcon, Building2, Hash, Layers,
} from 'lucide-react';
import JournalPreviewPanel from '../components/bill-review/JournalPreviewPanel';


const SEVERITY_TONE = {
  critical: 'bg-rose-50 text-rose-800 border-rose-300',
  high:     'bg-amber-50 text-amber-800 border-amber-300',
  medium:   'bg-amber-50 text-amber-700 border-amber-200',
  low:      'bg-sky-50 text-sky-700 border-sky-200',
  info:     'bg-slate-50 text-slate-600 border-slate-200',
};


function CheckItem({ check, testid, onUnlinkJournal }) {
  const Icon = check.ok ? CheckCircle2 : (
    check.severity === 'critical' ? XCircle
      : check.severity === 'info' ? Info : AlertTriangle
  );
  const tone = check.ok
    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
    : (SEVERITY_TONE[check.severity] || SEVERITY_TONE.info);
  return (
    <li
      className={`flex items-start gap-2 rounded-lg border px-2.5 py-1.5 text-[11px] ${tone}`}
      data-testid={testid}
    >
      <Icon className="w-3.5 h-3.5 mt-0.5 shrink-0" />
      <span className="leading-relaxed flex-1">
        {check.message_ar}
        {check.key === 'journal_status' && check.journal_id && (
          <>
            <Link
              to={`/journal?focus=${check.journal_id}`}
              className="ml-1 text-indigo-700 underline font-semibold"
              data-testid="journal-status-link"
            >
              افتَح
            </Link>
            {!check.ok && onUnlinkJournal && (
              <button
                onClick={onUnlinkJournal}
                data-testid="unlink-journal-btn"
                className="ml-2 text-[10px] rounded border border-rose-300
                            bg-rose-100 hover:bg-rose-200 text-rose-800
                            px-1.5 py-0.5 font-semibold"
                title="فَصل هَذِه الفاتورَة عَن القَيد (لِلحالات التي رُبِطَت فيها فاتورَتان بِخَطأ)"
              >
                فَصل القَيد
              </button>
            )}
          </>
        )}
      </span>
    </li>
  );
}


function SourceDocumentZone({ document: doc, bill }) {
  if (!doc) {
    return (
      <div className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-4 text-center text-xs text-slate-500"
           data-testid="zone-source">
        <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
        لا يُوجَد مُستَنَد مَصدَر مُرتَبِط.
      </div>
    );
  }
  const previewUrl = `${axiosInstance.defaults.baseURL}/document-hub/${doc.id}/preview`;
  const isPdf   = (doc.mime_type || '').includes('pdf');
  const isImage = (doc.mime_type || '').startsWith('image/');
  const ext = doc.extraction || {};
  return (
    <div className="rounded-xl border bg-white overflow-hidden flex flex-col" data-testid="zone-source">
      <div className="bg-slate-100 px-3 py-2 border-b flex items-center justify-between gap-1">
        <span className="text-[11px] font-bold text-slate-700 inline-flex items-center gap-1">
          <FileText className="w-3.5 h-3.5" /> المُستَنَد المَصدَر
        </span>
        <Link to={`/document-hub`} className="text-[10px] text-indigo-600 hover:underline">
          فَتح في مَركَز المُستَنَدات
        </Link>
      </div>
      <div className="p-2 flex-1 min-h-[360px] bg-slate-50 flex items-center justify-center">
        {isPdf ? (
          <iframe src={previewUrl} title={doc.file_name}
                   className="w-full h-full min-h-[360px] bg-white rounded shadow" />
        ) : isImage ? (
          <img src={previewUrl} alt={doc.file_name}
                className="max-w-full max-h-96 object-contain" />
        ) : (
          <div className="text-center text-slate-500 text-xs">
            <FileText className="w-12 h-12 mx-auto mb-2 opacity-40" />
            <div className="font-mono">{doc.file_name}</div>
          </div>
        )}
      </div>
      {ext.ocr && (
        <details className="border-t p-2 text-[10.5px]">
          <summary className="cursor-pointer font-semibold text-slate-600">
            OCR ({ext.ocr.method} · {ext.ocr.text_length || 0} حَرف)
          </summary>
          <pre className="whitespace-pre-wrap bg-slate-50 rounded p-2 mt-1 max-h-32 overflow-y-auto font-mono text-slate-700">
            {ext.ocr.text_preview}
          </pre>
        </details>
      )}
    </div>
  );
}


function SupplierZone({ report, testid = 'zone-supplier' }) {
  const s = report?.supplier || {};
  const statusPill = report?.status === 'ok'
    ? 'bg-emerald-100 text-emerald-800'
    : report?.status === 'needs_review'
      ? 'bg-amber-100 text-amber-800'
      : 'bg-rose-100 text-rose-800';
  return (
    <div className="rounded-xl border bg-white p-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-bold text-slate-700 inline-flex items-center gap-1">
          <ShieldCheck className="w-3.5 h-3.5" /> التَّحَقُّق مِن المورد
        </span>
        <span className={`text-[10px] font-semibold rounded px-2 py-0.5 ${statusPill}`}
               data-testid="supplier-status">
          {report?.status === 'ok' ? 'جاهِز'
            : report?.status === 'needs_review' ? 'يَحتاج مُراجَعَة'
              : 'مانِع'}
        </span>
      </div>
      <div className="text-[11px] text-slate-700 mb-2">
        <span className="text-slate-500">المُورِد:</span>{' '}
        <span className="font-semibold">{s.name_english || s.name_arabic || '—'}</span>
        {s.vat_number && (
          <span className="ml-2 font-mono text-[10px] text-slate-500">VAT: {s.vat_number}</span>
        )}
      </div>
      <ul className="space-y-1">
        {(report?.checks || []).map((c, i) => (
          <CheckItem key={i} check={c}
                      testid={`supplier-check-${c.key}`} />
        ))}
      </ul>
    </div>
  );
}


function CodingZone({ coding, bill, onApply, applying }) {
  const winner = coding?.winner;
  const [sel, setSel] = useState({});
  useEffect(() => {
    if (winner) {
      setSel({
        expense_account_id:   winner.expense_account_id,
        expense_account_code: winner.expense_account_code,
        expense_account_name: winner.expense_account_name,
        cost_center_id:       winner.cost_center_id,
        project_id:           winner.project_id,
        vat_rate:             winner.vat_rate,
        vat_code:             winner.vat_code,
      });
    } else {
      setSel({
        expense_account_id:   bill?.expense_account_id || '',
        expense_account_code: bill?.expense_account_code || '',
        cost_center_id:       bill?.cost_center_id || '',
      });
    }
  }, [winner, bill?.id]);

  const TIER_LABEL_AR = {
    rules:   'قاعِدَة نِظام',
    history: 'سِجِل تاريخي',
    llm:     'ذَكاء اصطِناعي',
  };
  const TIER_COLOR = {
    rules:   'bg-emerald-100 text-emerald-800',
    history: 'bg-sky-100 text-sky-800',
    llm:     'bg-violet-100 text-violet-800',
  };

  return (
    <div className="rounded-xl border bg-white p-3" data-testid="zone-coding">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-bold text-slate-700 inline-flex items-center gap-1">
          <Calculator className="w-3.5 h-3.5" /> التَّصنيف المُحاسَبي
        </span>
        {winner && (
          <span className={`text-[10px] font-semibold rounded px-2 py-0.5 ${TIER_COLOR[winner.tier]}`}
                 data-testid="coding-tier">
            {TIER_LABEL_AR[winner.tier]} · {((winner.confidence || 0) * 100).toFixed(0)}%
          </span>
        )}
      </div>

      {winner && (
        <div className="rounded bg-slate-50 border p-2 text-[10.5px] text-slate-700 mb-2"
             data-testid="coding-reasoning">
          <Sparkles className="w-3 h-3 inline mr-1 text-violet-500" />
          <span className="font-semibold">لماذا؟</span> {winner.reasoning_ar}
        </div>
      )}
      {!winner && (
        <div className="rounded bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-800 mb-2"
             data-testid="coding-empty">
          لا اقتِراحات تِلقائِيَّة — أَدخِل التَّصنيف يَدَوِيّاً.
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <label className="block">
          <span className="text-slate-500">رَمز حِساب المَصروف</span>
          <input value={sel.expense_account_code || ''}
                  onChange={(e) => setSel({ ...sel,
                    expense_account_code: e.target.value,
                    expense_account_id:   e.target.value })}
                  data-testid="coding-account-code"
                  className="w-full mt-0.5 rounded border border-slate-300 px-2 py-1 text-xs font-mono" />
        </label>
        <label className="block">
          <span className="text-slate-500">اسم الحِساب</span>
          <input value={sel.expense_account_name || ''}
                  onChange={(e) => setSel({ ...sel, expense_account_name: e.target.value })}
                  data-testid="coding-account-name"
                  className="w-full mt-0.5 rounded border border-slate-300 px-2 py-1 text-xs" />
        </label>
        <label className="block">
          <span className="text-slate-500">مَركَز التَّكلُفَة</span>
          <input value={sel.cost_center_id || ''}
                  onChange={(e) => setSel({ ...sel, cost_center_id: e.target.value })}
                  data-testid="coding-cost-center"
                  className="w-full mt-0.5 rounded border border-slate-300 px-2 py-1 text-xs font-mono" />
        </label>
        <label className="block">
          <span className="text-slate-500">المَشروع</span>
          <input value={sel.project_id || ''}
                  onChange={(e) => setSel({ ...sel, project_id: e.target.value })}
                  data-testid="coding-project"
                  className="w-full mt-0.5 rounded border border-slate-300 px-2 py-1 text-xs font-mono" />
        </label>
        <label className="block">
          <span className="text-slate-500">نِسبَة الضَّريبَة</span>
          <input type="number" step="0.01" value={sel.vat_rate ?? ''}
                  onChange={(e) => setSel({ ...sel, vat_rate: parseFloat(e.target.value) })}
                  data-testid="coding-vat-rate"
                  className="w-full mt-0.5 rounded border border-slate-300 px-2 py-1 text-xs font-mono" />
        </label>
        <label className="block">
          <span className="text-slate-500">رَمز الضَّريبَة</span>
          <input value={sel.vat_code || ''}
                  onChange={(e) => setSel({ ...sel, vat_code: e.target.value })}
                  data-testid="coding-vat-code"
                  className="w-full mt-0.5 rounded border border-slate-300 px-2 py-1 text-xs font-mono" />
        </label>
      </div>

      <button
        onClick={() => onApply(sel)} disabled={applying}
        data-testid="coding-apply-btn"
        className="mt-3 w-full text-xs font-semibold rounded-lg bg-emerald-600
                   hover:bg-emerald-700 text-white px-3 py-1.5 inline-flex
                   items-center justify-center gap-1 disabled:opacity-50"
      >
        {applying
          ? <Loader2 className="w-3 h-3 animate-spin" />
          : <CheckCircle2 className="w-3 h-3" />}
        تَطبيق التَّصنيف
      </button>
    </div>
  );
}


function ValidationZone({ report, onUnlinkJournal }) {
  return (
    <div className="rounded-xl border bg-white p-3" data-testid="zone-validation">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-bold text-slate-700 inline-flex items-center gap-1">
          <ListChecks className="w-3.5 h-3.5" /> مُحَرِّك التَّحَقُّق
        </span>
        <span className="text-[10px] font-mono text-slate-500" data-testid="validation-counter">
          {report?.passed || 0}/{report?.total || 0} ✓
        </span>
      </div>
      <ul className="space-y-1">
        {(report?.checks || []).map((c, i) => (
          <CheckItem key={i} check={c} testid={`validation-check-${c.key}`}
                      onUnlinkJournal={c.key === 'journal_status'
                        ? onUnlinkJournal : undefined} />
        ))}
      </ul>
    </div>
  );
}


function DiffZone({ diff }) {
  return (
    <div className="rounded-xl border bg-white p-3" data-testid="zone-diff">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-bold text-slate-700 inline-flex items-center gap-1">
          <GitCompare className="w-3.5 h-3.5" /> مُقارَنَة الاختِلافات
        </span>
        <span className="text-[10px] text-slate-500" data-testid="diff-changes">
          {diff?.total_changes || 0} تَغيير
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[10.5px]">
          <thead>
            <tr className="text-slate-500 border-b">
              <th className="text-right py-1 px-1">الحَقل</th>
              <th className="text-right py-1 px-1">OCR</th>
              <th className="text-right py-1 px-1">تَصحيح</th>
              <th className="text-right py-1 px-1">النِّهائي</th>
            </tr>
          </thead>
          <tbody>
            {(diff?.rows || []).map((r) => (
              <tr key={r.field} className="border-b last:border-b-0"
                  data-testid={`diff-row-${r.field}`}>
                <td className="py-1 px-1 font-semibold">{r.label_ar}</td>
                <td className={`py-1 px-1 font-mono ${
                  r.changed_ocr_to_correction ? 'bg-rose-50 text-rose-700' : ''
                }`}>{r.ocr_value ?? '—'}</td>
                <td className={`py-1 px-1 font-mono ${
                  r.changed_ocr_to_correction ? 'bg-amber-50 text-amber-700' : ''
                }`}>{r.corrected_value ?? '—'}</td>
                <td className={`py-1 px-1 font-mono ${
                  r.changed_correction_to_final ? 'bg-sky-50 text-sky-700 font-semibold' : ''
                }`}>{r.final_value ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function SummaryZone({ summary, onVerify, verifying, canVerify }) {
  const s = summary || {};
  const pillOK  = 'bg-emerald-100 text-emerald-800 font-semibold';
  const pillBad = 'bg-rose-100 text-rose-700';
  const readinessRow = (label, ok, testid) => (
    <div className="flex items-center justify-between py-1 border-b last:border-b-0">
      <span className="text-[11px]">{label}</span>
      <span className={`text-[10px] rounded px-1.5 py-0.5 ${ok ? pillOK : pillBad}`}
             data-testid={testid}>
        {ok ? 'جاهِز' : 'غَير جاهِز'}
      </span>
    </div>
  );
  return (
    <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50/40 p-3"
         data-testid="zone-summary">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-bold text-emerald-900 inline-flex items-center gap-1">
          <CheckCircle2 className="w-3.5 h-3.5" /> ملَخَّص التَّحَقُّق
        </span>
        <span className="text-[18px] font-bold text-emerald-800 font-mono"
               data-testid="summary-quality">
          {(s.document_quality ?? 0).toFixed(0)}%
        </span>
      </div>
      {readinessRow('جودَة المُستَنَد',
                     (s.document_quality || 0) >= 70, 'summary-quality-pill')}
      {readinessRow('المُورِد',      s.supplier_ready,   'summary-supplier-pill')}
      {readinessRow('التَّصنيف المُحاسَبي', s.accounting_ready, 'summary-accounting-pill')}
      {readinessRow('الضَّريبَة',      s.vat_ready,        'summary-vat-pill')}
      {readinessRow('القَيد',        s.journal_ready,    'summary-journal-pill')}
      <button
        onClick={onVerify} disabled={verifying || !canVerify}
        data-testid="summary-verify-btn"
        className="mt-3 w-full text-sm font-bold rounded-lg bg-emerald-600
                   hover:bg-emerald-700 text-white px-3 py-2.5 inline-flex
                   items-center justify-center gap-1.5 disabled:opacity-40
                   disabled:cursor-not-allowed"
      >
        {verifying
          ? <Loader2 className="w-4 h-4 animate-spin" />
          : <CheckCircle2 className="w-4 h-4" />}
        تَحَقُّق وَإنشاء مُعايَنَة القَيد
      </button>
      {s.verification_state === 'verified' && (
        <div className="mt-2 text-center text-[10.5px] text-emerald-700 font-semibold"
             data-testid="summary-verified-badge">
          ✓ تَم التَّحَقُّق — جاهِز لِـ Journal Preview (F3-B/3)
        </div>
      )}
    </div>
  );
}


export default function BillReviewConsole() {
  const { billId } = useParams();
  const navigate = useNavigate();
  const [bundle, setBundle]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [applying, setApplying] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(`/bill-review/${billId}`);
      if (data.error === 'not_found') {
        toast.error('الفاتورَة غَير مَوجودَة');
        navigate('/document-hub');
        return;
      }
      setBundle(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فَشَل تَحميل لَوحَة المُراجَعَة');
    } finally { setLoading(false); }
  }, [billId, navigate]);

  useEffect(() => { refresh(); }, [refresh]);

  const applyCoding = async (payload) => {
    setApplying(true);
    try {
      await axiosInstance.post(`/bill-review/${billId}/apply-coding`, payload);
      toast.success('تَم تَطبيق التَّصنيف');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فَشَل التَّطبيق');
    } finally { setApplying(false); }
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const { data } = await axiosInstance.post(`/bill-review/${billId}/verify`);
      if (data.ok) {
        toast.success('تَمَّ التَّحَقُّق — جاهِز لِـ Journal Preview');
      } else {
        toast.error('لَم يَجتَز التَّحَقُّق — راجِع القائِمَة');
      }
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فَشَل التَّحَقُّق');
    } finally { setVerifying(false); }
  };

  const unlinkJournal = async () => {
    const reason = window.prompt(
      'سَبَب فَصل الفاتورَة عَن القَيد الحالي؟\n(5 حُروف على الأَقَل — سيُحفَظ في سِجِل التَّدقيق)'
    );
    if (!reason || reason.trim().length < 5) return;
    try {
      const { data } = await axiosInstance.post(
        `/bill-review/${billId}/unlink-journal`, { reason });
      if (data.ok) {
        toast.success(data.message_ar || 'تَم فَصل القَيد بِنَجاح');
        await refresh();
      } else if (data.error === 'sole_owner') {
        toast.error(data.message || 'الفاتورَة هي الوَحيدَة المُرتَبِطَة بالقَيد');
      } else {
        toast.error(data.message || data.error || 'فَشَل فَصل القَيد');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فَشَل فَصل القَيد');
    }
  };

  if (loading || !bundle) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const canVerify = bundle.validation?.can_verify;
  const bill = bundle.bill;

  return (
    <div className="min-h-screen bg-slate-100 p-4" dir="rtl" data-testid="bill-review-console">
      {/* Header */}
      <div className="max-w-[1600px] mx-auto flex items-center justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2 text-slate-500 text-xs">
            <button onClick={() => navigate(-1)}
                     className="hover:text-indigo-600 inline-flex items-center gap-1"
                     data-testid="back-btn">
              <ArrowLeft className="w-3.5 h-3.5" /> رُجوع
            </button>
            <span>›</span>
            <span>لَوحَة مُراجَعَة الفاتورَة</span>
          </div>
          <h1 className="text-lg font-bold text-slate-800 inline-flex items-center gap-2">
            {bill.bill_number}
            <span className="text-[10px] font-mono bg-slate-200 rounded px-1.5 py-0.5">
              {bill.status}
            </span>
            {bill.financial_lifecycle_id && (
              <span className="text-[10px] font-mono bg-indigo-100 text-indigo-800
                                rounded px-1.5 py-0.5 border border-indigo-200"
                     data-testid="fl-id-badge" title="Financial Lifecycle ID">
                {bill.financial_lifecycle_id}
              </span>
            )}
          </h1>
        </div>
        <div className="text-left text-[11px]">
          <div className="text-slate-500">المُورِد</div>
          <div className="font-semibold">{bill.supplier_name || '—'}</div>
        </div>
      </div>

      {/* Grid: 6 zones */}
      <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Left column: Source Document (Zone 1) — tall */}
        <div className="lg:row-span-2">
          <SourceDocumentZone document={bundle.document} bill={bill} />
        </div>
        {/* Middle column: Supplier + Coding */}
        <div className="space-y-3">
          <SupplierZone report={bundle.supplier} />
          <CodingZone
            coding={bundle.coding}
            bill={bundle.bill}
            onApply={applyCoding}
            applying={applying}
          />
        </div>
        {/* Right column: Validation + Summary + Journal Preview */}
        <div className="space-y-3">
          <ValidationZone report={bundle.validation}
                           onUnlinkJournal={unlinkJournal} />
          <SummaryZone
            summary={bundle.summary}
            onVerify={verify}
            verifying={verifying}
            canVerify={canVerify}
          />
          {(['verified', 'posted'].includes(bundle.summary?.verification_state)) && (
            <JournalPreviewPanel billId={billId} onPosted={refresh} />
          )}
        </div>
        {/* Full-width diff row */}
        <div className="lg:col-span-3">
          <DiffZone diff={bundle.diff} />
        </div>
      </div>
    </div>
  );
}
